import axiosInstance from '../axiosInstance';
import { recordAfterPush } from './pushResult';
import { initializeDB } from '../config/db';
import {
    TIMESHEET_RECORD_STORE,
    DAILY_JOB_NOTE_STORE,
    SIGNED_TIMESHEET_STORE,
} from '../config/constants';
import timesheetService, { SYNC_STATUS, MAX_SYNC_RETRIES, EVT_TIMESHEET_CHANGED } from './timesheetService';
import { decodeOthersHours } from './othersHoursCodec';

/**
 * Сервис синхронизации таймшита с сервером.
 *
 * Архитектура:
 *  - Push: берёт записи с syncStatus=pending и шлёт на сервер батчем.
 *    На сервере идемпотентность через clientId (UPSERT).
 *  - Pull: периодически опрашивает сервер на изменения, делает merge по LWW (updatedAt).
 *  - Offline: слушает online/offline + visibilitychange, запускает/останавливает
 *    фоновый цикл, форсирует sync при возврате в фокус.
 *  - Retry: exponential backoff, MAX_SYNC_RETRIES попыток на одну "волну" → status=error.
 *  - Heal: каждые HEAL_INTERVAL_MS цикл автоматически переводит записи из error
 *    обратно в pending (до HEAL_MAX_CYCLES раз) — чтобы временная сеть / протухший
 *    токен не оставляли записи навсегда застрявшими без участия пользователя.
 *
 * Серверные error-коды (видимые в `response.errors[*].code`):
 *  - DAILY_LIMIT_EXCEEDED → помечаем запись как CONFLICT с payload.details.existingRecords;
 *                           не retry'им — это data conflict, требует UI resolution.
 *  - BATCH_TOO_LARGE      → уменьшаем BATCH_SIZE пополам на следующую волну.
 *  - остальные            → общая ретраевая логика.
 */

const SYNC_INTERVAL_MS = 15_000;          // fallback-тик каждые 15 сек (write-through save триггерит раньше)
// 1 сек тишины → отправляем (10/15: было 3). Тап на клавиатуре айпада сразу
// ставит итоговое число — «допечатывания» почти нет; быстрые тапы по соседним
// клеткам всё равно уходят одной пачкой, потолок ожидания — KICK_MAX_WAIT_MS.
const KICK_DEBOUNCE_MS = 1_000;
                                          // (для 40+ foreman с iPad 2 сек мало — тап стрелок/шаг/пауза "подумать")
const KICK_MAX_WAIT_MS = 30_000;          // но максимум 30 сек: если пишет непрерывно 30 сек — шлём всё равно
const HEAL_INTERVAL_MS = 5 * 60_000;      // каждые 5 мин воскрешаем error→pending
const PULL_THROTTLE_MS = 60_000;          // не чаще раза в минуту pull'им серверные изменения
const LAST_PULL_KEY = 'timesheetLastPullAt';
const HEAL_MAX_CYCLES = 10;               // 10 циклов × 5 retries = до 50 попыток доставки
const INITIAL_BATCH_SIZE = 100;           // стартовый размер батча push
const MIN_BATCH_SIZE = 10;                // ниже не опускаемся
const BACKOFF_BASE_MS = 5_000;            // 5, 10, 20, 40, 80 сек exponential
const BACKOFF_MAX_MS = 5 * 60_000;        // потолок 5 мин между попытками
const EVT_SYNC_STATE = 'timesheetSyncStateChanged';
/** Эмитится только когда sync реально что-то изменил в IndexedDB — UI
 *  перечитывает видимую дату чтобы цвет статуса (жёлтый/зелёный/красный)
 *  соответствовал реальному syncStatus в БД. */
const EVT_SYNC_COMPLETED = 'timesheetSyncCompleted';

// ─── Каталог cost-кодов работ (внеш. таблица JobCode) ────────────────────
// Источник истины — внешняя БД. Каталог приезжает pull'ом (includeJobCodes=1,
// раз в TTL) и кэшируется в localStorage; add/delete копятся в очереди ops и
// уезжают в POST /sync (сервер идемпотентен, чистим очередь по processedOps).
const JOB_CODES_KEY = 'timesheetJobCodes';            // [{job, code, description}]
const JOB_CODES_TS_KEY = 'timesheetJobCodes:ts';
// Каталог правят в Document Manager — освежаем раз в час, как остальные данные,
// и сразу при синке устройства (syncAllData сбрасывает метку). Было 6 часов:
// удалённые в DM коды висели на айпадах полдня (10/2026).
const JOB_CODES_TTL_MS = 5 * 60 * 1000;
const JOB_CODE_OPS_KEY = 'timesheetJobCodeOpsQueue';  // [{opId, op, job, code, description}]
const EVT_JOB_CODES_CHANGED = 'timesheetJobCodesChanged';
// Справочник работ пополнился (создан код работы 7888-1) — экраны перечитывают работы.
export const EVT_JOBS_CHANGED = 'timesheetJobsChanged';

// Рабочая неделя приходит с СЕРВЕРА (админ открывает и закрывает недели), а не
// вычисляется от даты устройства. Приезжает в каждом pull'е и кэшируется, чтобы
// офлайн-запуск знал, с какой неделей работать.
//   { weekEnding, weekStart, openedAt, closedWeeks: [...] }
// weekEnding = null — админ ещё не начал неделю: приложение живёт по своей дате.
const ACTIVE_WEEK_KEY = 'timesheetActiveWeek';
// Флаг разовой чистки чужих записей. Версия в имени: если правило владения
// когда-нибудь поменяется, чистку можно будет прогнать заново новым ключом.
// v2: первая чистка отработала, пока сервер ещё отдавал чужие записи админам
// (ROLE_OFFICE обходил правило владения) — и чужое приезжало обратно уже после
// неё, а флаг больше не давал перечистить. Новый ключ = один повторный проход.
// v3: чистим ещё и чужие подписи и заметки (10/30: чужой лист под тем же clientId
// перезаписывал чужую подпись).
const FOREIGN_PURGE_KEY = 'timesheetForeignPurged:v3';
const EVT_ACTIVE_WEEK_CHANGED = 'timesheetActiveWeekChanged';
// Часы ДРУГИХ форманов по людям листа: { [weekEnding]: rows }. Выжимка только для
// показа (колонка Other, предупреждение при вводе). В базу записей НЕ кладём:
// когда чужие записи жили рядом со своими, правка ячейки уходила в чужую строку
// и часы подменялись/дублировались. Здесь ячейкам трогать нечего.
// Курсорный pull: по неделе — { [weekEnding]: { scope, cursor, fullAt, notesStamp,
// signedStamp } }, для общего pull'а — один курсор. Привязаны к userId.
const WEEK_SYNC_KEY = 'timesheetWeekSync';
const WEEK_SYNC_WEEKS = 8;
const GLOBAL_CURSOR_KEY = 'timesheetPullCursor';
const WEEK_FULL_RESYNC_MS = 6 * 60 * 60 * 1000;   // неделю целиком — не чаще раза в 6 часов
const MAX_PULL_PAGES = 40;                        // 40 × 500 записей — потолок одного pull'а
const OTHERS_HOURS_KEY = 'timesheetOthersHours';
const OTHERS_HOURS_WEEKS = 3;
const EVT_OTHERS_HOURS_CHANGED = 'timesheetOthersHoursChanged';

// Зависшие конфликты перечитаны с сервера — страница сообщает об этом.
const EVT_CONFLICTS_RESOLVED = 'timesheetConflictsResolved';

class TimesheetSyncService {
    constructor() {
        this.isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
        this.isSyncing = false;
        this.syncInterval = null;
        this.healInterval = null;
        this.backoffUntil = 0;
        // Dynamic batch size — shrinks on BATCH_TOO_LARGE, restores on clean tick
        this.currentBatchSize = INITIAL_BATCH_SIZE;
        // Счётчики для UI / getStatus()
        this.pendingCount = 0;
        this.errorCount = 0;
        this.conflictCount = 0;

        this.handleOnline = this.handleOnline.bind(this);
        this.handleOffline = this.handleOffline.bind(this);
        this.handleVisibility = this.handleVisibility.bind(this);
        this.handleRecordChanged = this.handleRecordChanged.bind(this);
        this.handleFocusOut = this.handleFocusOut.bind(this);
        this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
        this.tick = this.tick.bind(this);
        this.healTick = this.healTick.bind(this);
        this._kickTimer = null;
        this._kickFirstAt = null;   // первый save в текущей "сессии редактирования"
        this._tickAgain = false;    // толчок пришёл посреди цикла — повторить сразу после
    }

    init() {
        if (typeof window === 'undefined') return;
        window.addEventListener('online', this.handleOnline);
        window.addEventListener('offline', this.handleOffline);
        window.addEventListener(EVT_TIMESHEET_CHANGED, this.handleRecordChanged);
        window.addEventListener('beforeunload', this.handleBeforeUnload);
        document.addEventListener('visibilitychange', this.handleVisibility);
        // focusout поднимается, blur — нет. Слушаем focusout в capture phase,
        // чтобы ловить уход фокуса из любого input/textarea/select в приложении.
        document.addEventListener('focusout', this.handleFocusOut, true);

        // Первый запуск: чтобы pullServerUpdates не запросил ВСЮ историю у сервера,
        // ставим lastPullAt = now. Следующий pull будет с since=now → только свежие
        // изменения (reject/approve от админа) попадут в клиент.
        if (!localStorage.getItem(LAST_PULL_KEY)) {
            localStorage.setItem(LAST_PULL_KEY, new Date().toISOString());
        }

        // РАЗОВАЯ чистка чужих записей (см. purgeForeignRecords).
        purgeForeignRecords()
            .then(n => { if (n > 0) console.info(`[TimesheetSync] init: purged ${n} foreign record(s)`); })
            .catch(err => console.warn('[TimesheetSync] init purge failed:', err));

        // Отправка, прерванная перезагрузкой (обновление приложения, закрытие
        // вкладки), оставляла записи «в пути» навсегда: в очередь их больше
        // никто не брал. При старте ничего в пути быть не может — назад в очередь.
        // Потом — разбираем зависшие конфликты (см. resolveConflicts).
        resetStuckSyncing()
            .then(n => { if (n > 0) console.info(`[TimesheetSync] init: ${n} interrupted syncing→pending`); })
            .catch(err => console.warn('[TimesheetSync] init syncing reset failed:', err))
            .finally(() => this.resolveConflicts().catch(err => console.warn('[TimesheetSync] resolve conflicts failed:', err)));

        // Воскрешаем зависшие error-записи при старте — типичный кейс: вчера сервер
        // лежал, записи ушли в error, сегодня приложение открывается заново.
        timesheetService.resetErrorsToRetry({ maxCycles: HEAL_MAX_CYCLES })
            .then(({ reset }) => {
                if (reset > 0) {
                    console.info(`[TimesheetSync] init: resurrected ${reset} error→pending`);
                }
            })
            .catch(err => console.warn('[TimesheetSync] init heal failed:', err));

        if (this.isOnline) {
            this.startInterval();
            this.startHealInterval();
        }
        // Первая попытка сразу — но неблокирующе и с задержкой чтобы heal успел
        setTimeout(() => this.tick(), 500);
    }

    destroy() {
        if (typeof window === 'undefined') return;
        window.removeEventListener('online', this.handleOnline);
        window.removeEventListener('offline', this.handleOffline);
        window.removeEventListener(EVT_TIMESHEET_CHANGED, this.handleRecordChanged);
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        document.removeEventListener('visibilitychange', this.handleVisibility);
        document.removeEventListener('focusout', this.handleFocusOut, true);
        if (this._kickTimer) { clearTimeout(this._kickTimer); this._kickTimer = null; }
        this.stopInterval();
        this.stopHealInterval();
    }

    /**
     * Фокус ушёл из input/textarea/select → юзер "закончил" с этим полем.
     * Если на руках есть отложенный save (debounce таймер) — форсируем sync
     * немедленно. Юзер видит результат сразу, не ждёт 3 сек.
     */
    handleFocusOut(e) {
        const el = e.target;
        if (!el || !el.tagName) return;
        if (!['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)) return;
        // Только когда есть pending save — иначе это просто фокус шатается по странице
        if (this._kickTimer === null) return;
        clearTimeout(this._kickTimer);
        this._kickTimer = null;
        this._kickFirstAt = null;
        if (this.isOnline) {
            this.backoffUntil = 0;
            // Даём React домонтировать/пересохранить последнее значение (onBlur→setState→save)
            setTimeout(() => this.tick(), 50);
        }
    }

    /**
     * Юзер закрывает вкладку / закрывает iPad-приложение. Если есть pending —
     * отправляем через sendBeacon (HTTP без ожидания ответа, работает даже
     * во время unload). Не блокирует закрытие, запись уходит в фоне.
     */
    handleBeforeUnload() {
        if (!this.isOnline || typeof navigator === 'undefined' || !navigator.sendBeacon) return;
        // Отменяем любые debounce-таймеры — они уже не сработают
        if (this._kickTimer) { clearTimeout(this._kickTimer); this._kickTimer = null; }
        // Не await — unload не даст времени
        this._beaconPending().catch(() => {});
    }

    /** Собрать pending из всех stores и отправить sendBeacon'ом. Best-effort. */
    async _beaconPending() {
        try {
            const budget = 100;
            const records = await timesheetService.getRecordsBySyncStatus(SYNC_STATUS.PENDING, TIMESHEET_RECORD_STORE, budget);
            const notes   = await timesheetService.getRecordsBySyncStatus(SYNC_STATUS.PENDING, DAILY_JOB_NOTE_STORE, budget - records.length);
            const signed  = await timesheetService.getRecordsBySyncStatus(SYNC_STATUS.PENDING, SIGNED_TIMESHEET_STORE, Math.max(0, budget - records.length - notes.length));
            const total = records.length + notes.length + signed.length;
            if (total === 0) return;

            const payload = {
                records: records.map(toServerShape),
                dailyNotes: notes.map(toServerShape),
                signedTimesheets: signed.map(stripLocalOnlyFields),
            };
            // axios baseURL — http://admin.xxx.local/api, достраиваем путь вручную
            const base = axiosInstance.defaults.baseURL || '';
            const url = base.replace(/\/+$/, '') + '/timesheet/sync';
            const token = localStorage.getItem('token');
            // sendBeacon не умеет custom headers → шлём JSON через Blob с типом.
            // Токен передаём через query (нестандартно, но сервер может попросить accept).
            // ЛУЧШЕ — fetch(keepalive: true), он поддерживает headers.
            if (typeof fetch === 'function') {
                fetch(url, {
                    method: 'POST',
                    keepalive: true,
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': token ? `Bearer ${token}` : '',
                    },
                    body: JSON.stringify(payload),
                }).catch(() => {});
                return;
            }
            // Фолбэк на sendBeacon (без авторизации — лучше чем ничего)
            const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
            navigator.sendBeacon(url, blob);
        } catch {
            // best-effort
        }
    }

    /**
     * Юзер только что сохранил запись → через KICK_DEBOUNCE_MS тишины пинаем sync.
     * Это debounce-with-max-wait:
     *   - Каждый новый save сдвигает таймер на +KICK_DEBOUNCE_MS (юзер ещё печатает)
     *   - Но если суммарно с первого save прошло >= KICK_MAX_WAIT_MS — отправляем
     *     НЕЗАВИСИМО от того, что юзер ещё пишет, чтобы запись не висела часами.
     *
     * Например юзер пишет "9" (сохр.), 300мс "." (сохр.), 300мс "5" (сохр.),
     * 400мс табом уходит → 2000мс тишины → улетает "9.5" одним батчем.
     *
     * Если же юзер печатает без остановки 15 секунд (нереалистично, но возможно) —
     * через 10 сек отправляем текущее состояние, потом повторим через 2 сек после
     * последнего нового save.
     */
    handleRecordChanged() {
        if (!this.isOnline) return;
        const now = Date.now();
        if (this._kickFirstAt === null) {
            this._kickFirstAt = now;
        }
        if (this._kickTimer) clearTimeout(this._kickTimer);

        const waited = now - this._kickFirstAt;
        const delay = waited >= KICK_MAX_WAIT_MS
            ? 0                    // юзер слишком долго пишет — отправляем прямо сейчас
            : KICK_DEBOUNCE_MS;

        this._kickTimer = setTimeout(() => {
            this._kickTimer = null;
            this._kickFirstAt = null;
            // Сбрасываем backoff — юзер только что что-то сохранил, ждёт отправки
            this.backoffUntil = 0;
            this.tick();
        }, delay);
    }

    handleOnline() {
        this.isOnline = true;
        this.backoffUntil = 0;           // вернулась сеть — сбрасываем ожидание
        this.emitStateChange();
        this.startInterval();
        this.startHealInterval();
        // heal + tick подряд — сеть вернулась, пора разгрести очередь
        timesheetService.resetErrorsToRetry({ maxCycles: HEAL_MAX_CYCLES })
            .finally(() => this.tick());
    }

    handleOffline() {
        this.isOnline = false;
        this.emitStateChange();
        this.stopInterval();
        this.stopHealInterval();
    }

    /** Вернулась фокус/вкладка — триггерим sync немедленно (если онлайн). */
    handleVisibility() {
        if (typeof document === 'undefined') return;
        if (document.visibilityState === 'visible' && this.isOnline) {
            this.backoffUntil = 0;
            this.tick();
        }
    }

    startInterval() {
        if (this.syncInterval) return;
        this.syncInterval = setInterval(this.tick, SYNC_INTERVAL_MS);
    }
    stopInterval() {
        if (this.syncInterval) {
            clearInterval(this.syncInterval);
            this.syncInterval = null;
        }
    }
    startHealInterval() {
        if (this.healInterval) return;
        this.healInterval = setInterval(this.healTick, HEAL_INTERVAL_MS);
    }
    stopHealInterval() {
        if (this.healInterval) {
            clearInterval(this.healInterval);
            this.healInterval = null;
        }
    }

    emitStateChange() {
        if (typeof window === 'undefined') return;
        window.dispatchEvent(new CustomEvent(EVT_SYNC_STATE, {
            detail: {
                isOnline: this.isOnline,
                isSyncing: this.isSyncing,
                pendingCount: this.pendingCount,
                errorCount: this.errorCount,
                conflictCount: this.conflictCount,
                inBackoff: Date.now() < this.backoffUntil,
            },
        }));
    }

    async tick() {
        if (!this.isOnline) return;
        if (this.isSyncing) {
            // Толчок пришёл посреди цикла (часто — пока идёт pull) — не теряем его:
            // повторим сразу после. Раньше он пропадал, и свежие часы ждали
            // следующего тика до 15 сек.
            this._tickAgain = true;
            return;
        }
        if (Date.now() < this.backoffUntil) return;

        this.isSyncing = true;
        if (typeof window !== 'undefined') window.__timesheetSyncBusy = true; // автообновление ждёт конца отправки
        this.emitStateChange();
        let result = null;
        let pulledAnything = false;
        try {
            result = await this.syncPendingRecords();
            // Успешный tick — восстанавливаем полный размер батча
            this.currentBatchSize = INITIAL_BATCH_SIZE;

            // После push — pull серверных изменений (admin reject / approve / etc.).
            // Throttle'им: не чаще раза в минуту, чтобы сервер не долбить.
            const lastPullAt = Number(localStorage.getItem(LAST_PULL_KEY + ':ts') || 0);
            if (Date.now() - lastPullAt > PULL_THROTTLE_MS) {
                pulledAnything = await this.pullServerUpdates();
            }
        } catch (error) {
            console.error('[TimesheetSync] tick failed:', error);
            this.scheduleBackoff();
        } finally {
            this.isSyncing = false;
            if (typeof window !== 'undefined') window.__timesheetSyncBusy = false;
            await this.refreshCounters();
            this.emitStateChange();

            // Если что-то реально поменялось в IDB (push saved/conflicts/errors
            // или pull принёс апдейты) — уведомляем UI чтобы перечитал видимую дату.
            const pushChanged = result && (result.synced + result.conflicts + result.failed) > 0;
            if ((pushChanged || pulledAnything) && typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent(EVT_SYNC_COMPLETED, { detail: result || {} }));
            }
            if (this._tickAgain) {
                this._tickAgain = false;
                setTimeout(() => this.tick(), 0);
            }
        }
    }

    /**
     * Pull серверных изменений через /api/timesheet/updates. Use-case:
     *   - админ reject'нул запись → iPad должен увидеть reason
     *   - админ approved → status обновится на serverId-lookup side
     *   - foreman на другом iPad подписал — запись становится 'signed' здесь тоже
     *
     * Два режима:
     *   - weekEnding (YYYY-MM-DD): тянем ВСЕ записи этой недели БЕЗ since-фильтра.
     *     Это самое надёжное: открываем неделю в UI → получаем свежее состояние
     *     включая админские reject/approve, независимо от race'ов с push.
     *     Опциональные employeeIds/tempIds сужают выборку до листа бригадира,
     *     чтобы payload не упирался в PULL_MAX_ROWS=500 на больших компаниях.
     *   - без weekEnding: incremental pull по since (fallback для периодического tick).
     *
     * Merge через mergeSingleRecord — LWW по updatedAt.
     * Возвращает true если хоть что-то было влито в IDB.
     */
    async pullServerUpdates({ weekEnding = null, employeeIds = null, tempIds = null, forceFull = false } = {}) {
        try {
            // Используем URLSearchParams чтобы PHP получил `employeeIds[]=1&employeeIds[]=2`
            // (axios default передаёт `employeeIds=1&employeeIds=2`, что попадёт в
            // Request::query->get() как один scalar — фильтр не сработает).
            const qs = new URLSearchParams();
            // Курсорный pull (10/2026): раньше week-режим раз в 30+ секунд перекачивал
            // ВСЮ неделю (до 500 записей, ~380 КБ). Теперь — только изменившееся
            // после курсора, страницами. Целиком — при смене листа, раз в 6 часов
            // для страховки и когда локально на неделе пусто (данные стёрли).
            let weekState = null;
            let scopeKey = '';
            let full = false;
            if (weekEnding) {
                scopeKey = weekScopeKey(employeeIds, tempIds);
                weekState = getWeekSyncState(weekEnding);
                full = forceFull || !weekState || weekState.scope !== scopeKey
                    || (Date.now() - (weekState.fullAt || 0)) > WEEK_FULL_RESYNC_MS;
                qs.set('cursor', full ? '' : (weekState.cursor || ''));
                if (!full) {
                    if (weekState.notesStamp) qs.set('notesStamp', weekState.notesStamp);
                    if (weekState.signedStamp) qs.set('signedStamp', weekState.signedStamp);
                }
            } else {
                qs.set('cursor', getGlobalPullCursor());
            }
            if (weekEnding) {
                qs.set('weekEnding', weekEnding);
                if (Array.isArray(employeeIds)) {
                    for (const id of employeeIds) {
                        if (Number.isFinite(id) && id > 0) qs.append('employeeIds[]', String(id));
                    }
                }
                if (Array.isArray(tempIds)) {
                    for (const id of tempIds) {
                        if (Number.isFinite(id) && id > 0) qs.append('tempIds[]', String(id));
                    }
                }
                // Отпечаток нашего снимка чужих часов: не изменились — сервер их не шлёт.
                const stamp = getOthersStamp(weekEnding);
                if (stamp) qs.set('othersStamp', stamp);
            } else {
                const since = localStorage.getItem(LAST_PULL_KEY);
                if (since) qs.set('since', since);
                // Сужаем incremental-pull до СВОЕГО ростера: тянем обновления только по
                // своим людям (сервер вернёт чужие часы по ним — для оранжевой колонки),
                // а не по всей компании. Пустой ростер → без фильтра (сервер деградирует
                // к прежнему поведению безопасно). 24ч-лимит между форманами гарантирует
                // сервер при сохранении (ensureDailyLimit, SELECT FOR UPDATE) — локальный
                // зеркало всей базы для этого не нужно.
                try {
                    const roster = await timesheetService.getRosterSubjectIds();
                    for (const id of roster.employeeIds) qs.append('employeeIds[]', String(id));
                    for (const id of roster.tempIds) qs.append('tempIds[]', String(id));
                } catch { /* нет ростера — тянем как раньше */ }
            }
            // Каталог cost-кодов: просим сервер приложить его, когда кэш пуст/протух.
            const codesTs = Number(localStorage.getItem(JOB_CODES_TS_KEY) || 0);
            if (!localStorage.getItem(JOB_CODES_KEY) || (Date.now() - codesTs) > JOB_CODES_TTL_MS) {
                qs.set('includeJobCodes', '1');
            }
            const url = '/timesheet/updates' + (qs.toString() ? '?' + qs.toString() : '');
            // Момент запроса: ответ мог уйти с сервера раньше, чем туда дошла наша
            // отправка, — тогда в нём старая версия записи (см. mergeSingleRecord).
            let requestedAt = new Date().toISOString();
            const resp = await axiosInstance.get(url);
            const data = resp.data || {};

            let merged = 0;
            const mergeRecords = async (list, askedAt) => {
                for (const rec of (list || [])) {
                    const r = await mergeSingleRecord(rec, askedAt);
                    if (r === 'merged' || r === 'conflict') merged++;
                }
            };
            await mergeRecords(data.records, requestedAt);

            // Остальные страницы — только записи (recordsOnly), курсор едет по ходу.
            // Старый сервер курсора не знает (cursor undefined) — работаем как раньше.
            let cursor = typeof data.cursor === 'string' ? data.cursor : null;
            let more = cursor !== null && !!data.hasMore;
            for (let page = 0; more && page < MAX_PULL_PAGES; page++) {
                const next = new URLSearchParams(qs);
                ['includeJobCodes', 'othersStamp', 'notesStamp', 'signedStamp'].forEach(k => next.delete(k));
                next.set('cursor', cursor);
                next.set('recordsOnly', '1');
                requestedAt = new Date().toISOString();
                const part = (await axiosInstance.get('/timesheet/updates?' + next.toString())).data || {};
                await mergeRecords(part.records, requestedAt);
                cursor = typeof part.cursor === 'string' ? part.cursor : cursor;
                more = !!part.hasMore;
            }
            // Signed timesheets — КРИТИЧНО: когда админ / сервер invalidate'ит подпись
            // (soft-delete при payroll-изменениях), клиент должен увидеть isDeleted=true,
            // иначе UI будет продолжать показывать "неделя подписана" после правки часов.
            for (const signed of (data.signedTimesheets || [])) {
                const r = await mergeSingleSigned(signed);
                if (r === 'merged' || r === 'conflict') merged++;
            }
            // Daily notes — админ может добавить/отредактировать заметку со своей стороны
            for (const note of (data.dailyNotes || [])) {
                const r = await mergeSingleNote(note);
                if (r === 'merged' || r === 'conflict') merged++;
            }

            // Чужие часы по людям листа за эту неделю. Только в week-режиме: там
            // сервер знает лист формана. rows = null — «без изменений», снимок
            // остаётся; othersHours = null — листа нет, показывать нечего.
            if (weekEnding && 'othersHours' in data) {
                const oh = data.othersHours;
                if (oh === null) saveOthersHours(weekEnding, { stamp: '', rows: [] });
                else if (oh && Array.isArray(oh.rows)) saveOthersHours(weekEnding, oh);
            }

            // Разрешения правки прошлых недель (админ открыл job+week). Сервер шлёт
            // ВЕСЬ актуальный набор — перезаписываем локальный список целиком.
            if (Array.isArray(data.editUnlocks)) {
                const keys = data.editUnlocks.map(u => `${u.jobId}|${u.weekEnding}`);
                localStorage.setItem('timesheetEditUnlocks', JSON.stringify(keys));
            }

            // Рабочая неделя от сервера. Сохраняем всегда, а событие шлём только
            // когда неделя реально сменилась — на него страница показывает
            // «New week started». Прошлые данные локально НЕ трогаем.
            if (data.activeWeek && typeof data.activeWeek === 'object') {
                const prev = getActiveWeekState();
                localStorage.setItem(ACTIVE_WEEK_KEY, JSON.stringify({
                    weekEnding: data.activeWeek.weekEnding || null,
                    weekStart: data.activeWeek.weekStart || null,
                    openedAt: data.activeWeek.openedAt || null,
                    closedWeeks: Array.isArray(data.activeWeek.closedWeeks) ? data.activeWeek.closedWeeks : [],
                }));
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent(EVT_ACTIVE_WEEK_CHANGED, {
                        detail: {
                            previous: prev.weekEnding || null,
                            current: data.activeWeek.weekEnding || null,
                            weekStart: data.activeWeek.weekStart || null,
                        },
                    }));
                }
            }

            // Каталог cost-кодов (внеш. JobCode): массив = свежий снапшот, null/отсутствие
            // = сервер не прикладывал (или внеш. БД недоступна) — кэш остаётся.
            if (Array.isArray(data.jobCodes)) {
                localStorage.setItem(JOB_CODES_KEY, JSON.stringify(data.jobCodes));
                localStorage.setItem(JOB_CODES_TS_KEY, String(Date.now()));
                if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(EVT_JOB_CODES_CHANGED));
            }

            // Курсор — только после того, как всё влито: оборвался pull посередине —
            // следующий начнёт с прежнего места, ничего не потеряв.
            if (cursor !== null) {
                if (weekEnding) {
                    saveWeekSyncState(weekEnding, {
                        scope: scopeKey,
                        cursor,
                        fullAt: full ? Date.now() : (weekState?.fullAt || Date.now()),
                        notesStamp: typeof data.notesStamp === 'string' ? data.notesStamp : '',
                        signedStamp: typeof data.signedStamp === 'string' ? data.signedStamp : '',
                    });
                } else {
                    setGlobalPullCursor(cursor);
                }
            }

            // Обновляем lastPullAt только для since-режима, чтобы week-mode
            // не сдвигал "последний инкрементальный pull" вперёд.
            if (!weekEnding && data.serverTime) {
                localStorage.setItem(LAST_PULL_KEY, data.serverTime);
                localStorage.setItem(LAST_PULL_KEY + ':ts', String(Date.now()));
            }
            return merged > 0;
        } catch (e) {
            if (e?.response?.status && e.response.status !== 304) {
                console.warn('[TimesheetSync] pull failed:', e.message);
            }
            return false;
        }
    }

    /** Периодический heal: error → pending (если не превысили maxCycles). */
    /**
     * Зависшие конфликты («N records need attention»): разрешить их было нечем,
     * флажок висел вечно. Сервер — источник правды: запись с конфликтом
     * перечитываем с сервера (полный pull её недели), а запись, которой на
     * сервере нет (он её не принял), убираем с устройства. Возвращает, сколько
     * записей поправлено.
     */
    async resolveConflicts() {
        if (!this.isOnline) return 0;
        const weeks = new Set();
        let fixed = 0;
        for (const store of [TIMESHEET_RECORD_STORE, DAILY_JOB_NOTE_STORE, SIGNED_TIMESHEET_STORE]) {
            const rows = await timesheetService.getRecordsBySyncStatus(SYNC_STATUS.CONFLICT, store);
            for (const r of rows) {
                fixed++;
                if (store === TIMESHEET_RECORD_STORE) {
                    if (r.workDate) weeks.add(timesheetService.getWeekEndingDate(r.workDate));
                    if (!r.serverId) {
                        await timesheetService.hardDeleteTimesheetRecord(r.clientId);
                        continue;
                    }
                }
                await persistToStore(store, {
                    ...r,
                    syncStatus: SYNC_STATUS.SYNCED,
                    syncConflictDetails: null,
                    syncErrorMessage: null,
                    syncErrorCode: null,
                });
            }
        }
        if (!fixed) return 0;
        // Полный pull недели перепишет эти записи серверной версией (они SYNCED).
        for (const weekEnding of weeks) {
            await this.pullServerUpdates({ weekEnding, forceFull: true }).catch(() => {});
        }
        await this.refreshCounters();
        this.emitStateChange();
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent(EVT_SYNC_COMPLETED, { detail: { conflictsResolved: fixed } }));
            window.dispatchEvent(new CustomEvent(EVT_CONFLICTS_RESOLVED, { detail: { count: fixed } }));
        }
        console.info(`[TimesheetSync] resolved ${fixed} conflict record(s) from the server`);
        return fixed;
    }

    async healTick() {
        if (!this.isOnline) return;
        try {
            await this.resolveConflicts();
            const { reset } = await timesheetService.resetErrorsToRetry({ maxCycles: HEAL_MAX_CYCLES });
            if (reset > 0) {
                console.info(`[TimesheetSync] heal: ${reset} error→pending, forcing sync`);
                this.backoffUntil = 0;
                await this.tick();
            }
        } catch (err) {
            console.warn('[TimesheetSync] heal tick failed:', err);
        }
    }

    scheduleBackoff(attemptCount = 1) {
        const delay = Math.min(BACKOFF_BASE_MS * (2 ** (attemptCount - 1)), BACKOFF_MAX_MS);
        this.backoffUntil = Date.now() + delay;
    }

    /**
     * Отправить все ожидающие записи на сервер батчем — records + dailyNotes + signedTimesheets.
     * Возвращает { synced, failed, conflicts } суммарно по трём типам.
     */
    async syncPendingRecords() {
        // Считаем бюджет батча на все три типа вместе, чтобы не раздувать payload
        const budget = this.currentBatchSize;
        const records      = await timesheetService.getRecordsBySyncStatus(SYNC_STATUS.PENDING, 'timesheetRecords', budget);
        const remAfterRecs = Math.max(0, budget - records.length);
        const notes        = remAfterRecs > 0 ? await timesheetService.getRecordsBySyncStatus(SYNC_STATUS.PENDING, 'dailyJobNotes', remAfterRecs) : [];
        const remAfterNtes = Math.max(0, remAfterRecs - notes.length);
        const signed       = remAfterNtes > 0 ? await timesheetService.getRecordsBySyncStatus(SYNC_STATUS.PENDING, 'signedTimesheets', remAfterNtes) : [];

        // Очередь операций каталога cost-кодов — едет тем же батчем (мелкая).
        const jobCodeOps = getJobCodeOpsQueue();

        const totalPending = records.length + notes.length + signed.length;
        if (totalPending === 0 && jobCodeOps.length === 0) return { synced: 0, failed: 0, conflicts: 0 };

        // Помечаем пачку как syncing в соответствующих stores
        await markSyncingAcrossStores({ records, notes, signed });

        try {
            const response = await axiosInstance.post('/timesheet/sync', {
                records: records.map(toServerShape),
                dailyNotes: notes.map(toServerShape),
                signedTimesheets: signed.map(stripLocalOnlyFields),
                ...(jobCodeOps.length ? { jobCodeOps } : {}),
            });

            const { saved = [], conflicts = [], errors = [] } = response.data || {};

            // Момент, когда данные этого устройства РЕАЛЬНО дошли до офиса —
            // показывается в шапке страницы таймшита («Sent to office»).
            if (saved.length > 0 || conflicts.length > 0) {
                try { localStorage.setItem('timesheetLastSentAt', new Date().toISOString()); } catch (e) { /* noop */ }
            }

            // Подтверждённые операции каталога убираем из очереди; остальные
            // (внеш. БД была недоступна и т.п.) поедут со следующим sync.
            const processedOps = new Set(response.data?.processedOps || []);
            if (processedOps.size > 0) {
                saveJobCodeOpsQueue(getJobCodeOpsQueue().filter(o => !processedOps.has(o.opId)));
            }
            // Созданные коды работ: сервер выдал номер (7888-1) и вернул карточку —
            // кладём её в справочник работ и в каталог кодов, чтобы код сразу
            // появился в списке работ и в Job notes.
            const createdJobs = response.data?.createdJobs || [];
            if (createdJobs.length > 0) {
                const cat = getJobCodeCatalog();
                for (const c of createdJobs) {
                    const job = c && c.job;
                    if (!job || !job.id) continue;
                    try { await timesheetService.putJob(job); } catch (e) { console.warn('[jobCodes] could not store job', e); }
                    if (job.parentJob && job.jobCodeNo && !cat.some(x => x.job === String(job.parentJob) && x.code === String(job.jobCodeNo))) {
                        cat.push({ job: String(job.parentJob), code: String(job.jobCodeNo), description: job.codeDescription || null });
                    }
                }
                localStorage.setItem(JOB_CODES_KEY, JSON.stringify(cat));
                window.dispatchEvent(new CustomEvent(EVT_JOB_CODES_CHANGED));
                // Код, заведённый из заметок сотрудника, сразу встаёт строкой в таймшит.
                const addToGrid = createdJobs
                    .filter(c => c && c.job && jobCodeOps.some(o => o.opId === c.opId && o.addToGrid))
                    .map(c => c.job.id);
                window.dispatchEvent(new CustomEvent(EVT_JOBS_CHANGED, { detail: { jobs: createdJobs.map(c => c.job), addToGrid } }));
            }

            // Карты для быстрого lookup по типу. Сервер возвращает type='record'|'dailyNote'|'signed'.
            const byType = {
                record:    { pending: records, store: 'timesheetRecords'  },
                dailyNote: { pending: notes,   store: 'dailyJobNotes'     },
                signed:    { pending: signed,  store: 'signedTimesheets'  },
            };
            const indexPending = (items) => new Map(items.map(r => [r.clientId, r]));
            byType.record.byId    = indexPending(records);
            byType.dailyNote.byId = indexPending(notes);
            byType.signed.byId    = indexPending(signed);

            const mentioned = new Set();

            // Успешно сохранённые
            for (const s of saved) {
                const slot = byType[s.type];
                if (!slot) continue;
                const local = slot.byId.get(s.clientId);
                if (!local) continue;
                mentioned.add(s.type + ':' + s.clientId);
                await persistAfterPush(slot.store, local, {
                    serverId: s.serverId,
                    syncStatus: SYNC_STATUS.SYNCED,
                    syncRetryCount: 0,
                    syncErrorMessage: null,
                    lastSyncAttempt: new Date().toISOString(),
                    // Письмо с PDF сервер уже отправил — снимаем галочку, иначе
                    // каждая следующая отправка этой подписи слала бы его снова.
                    ...(local.emailPdf ? { emailPdf: false } : {}),
                });
            }

            // Конфликты версий (LWW — сервер свежее)
            for (const c of conflicts) {
                const slot = byType[c.type];
                if (!slot) continue;
                const local = slot.byId.get(c.clientId);
                if (!local) continue;
                mentioned.add(c.type + ':' + c.clientId);

                // Дубль клетки: у этой клетки на сервере уже есть моя запись
                // (existingClientId), эта — лишняя. Если та запись есть на айпаде,
                // переносим в неё более свежие значения и убираем дубль, — в клетке
                // остаётся одна запись, и на сервере тоже. Иначе — обычный конфликт.
                if (c.code === 'DUPLICATE_CELL' && c.type === 'record' && c.existingClientId) {
                    const keep = await timesheetService.getTimesheetRecordById(c.existingClientId);
                    if (keep && !keep.isDeleted) {
                        if (String(local.updatedAt || '') > String(keep.updatedAt || '')) {
                            await timesheetService.saveTimesheetRecord({
                                ...keep,
                                hours: local.hours,
                                attendCode: local.attendCode || '',
                                jobCode: local.jobCode || '',
                                notes: local.notes || '',
                            }, { validateLimit: false });
                        }
                        await timesheetService.hardDeleteTimesheetRecord(local.clientId);
                        continue;
                    }
                }

                await persistAfterPush(slot.store, local, {
                    syncStatus: SYNC_STATUS.CONFLICT,
                    syncErrorMessage: c.reason || 'Version conflict',
                    syncConflictDetails: c,
                    lastSyncAttempt: new Date().toISOString(),
                });
            }

            // Ошибки: часть — "перманентные data errors" (не retry), часть — ретраевые.
            for (const err of errors) {
                const slot = byType[err.type];
                if (!slot) continue;
                const local = slot.byId.get(err.clientId);
                if (!local) continue;
                mentioned.add(err.type + ':' + err.clientId);

                if (err.code === 'DAILY_LIMIT_EXCEEDED') {
                    await persistAfterPush(slot.store, local, {
                        syncStatus: SYNC_STATUS.CONFLICT,
                        syncErrorMessage: err.message || 'Daily limit exceeded',
                        syncConflictDetails: err.details || null,
                        syncErrorCode: err.code,
                        lastSyncAttempt: new Date().toISOString(),
                    });
                    continue;
                }

                const nextRetry = (local.syncRetryCount || 0) + 1;
                const isFinal = nextRetry >= MAX_SYNC_RETRIES;
                await persistAfterPush(slot.store, local, {
                    syncStatus: isFinal ? SYNC_STATUS.ERROR : SYNC_STATUS.PENDING,
                    syncRetryCount: nextRetry,
                    syncErrorMessage: err.message || 'Server error',
                    syncErrorCode: err.code || null,
                    lastSyncAttempt: new Date().toISOString(),
                });
            }

            // Сервер не упомянул — возвращаем в pending (иначе застрянут в SYNCING)
            for (const typeKey of Object.keys(byType)) {
                const slot = byType[typeKey];
                for (const local of slot.pending) {
                    if (!mentioned.has(typeKey + ':' + local.clientId)) {
                        await persistAfterPush(slot.store, local, { syncStatus: SYNC_STATUS.PENDING });
                    }
                }
            }

            return { synced: saved.length, failed: errors.length, conflicts: conflicts.length };
        } catch (error) {
            const status = error?.response?.status;
            const errData = error?.response?.data;

            if (status === 413 && errData?.code === 'BATCH_TOO_LARGE') {
                const newSize = Math.max(MIN_BATCH_SIZE, Math.floor(this.currentBatchSize / 2));
                console.warn(`[TimesheetSync] batch too large, shrinking ${this.currentBatchSize} → ${newSize}`);
                this.currentBatchSize = newSize;
                await resetAllToPending({ records, notes, signed });
                return { synced: 0, failed: totalPending, conflicts: 0 };
            }

            // Сеть / сервер недоступны → retry во всех трёх stores
            const allItems = [
                ...records.map(r => ({ store: 'timesheetRecords',  rec: r })),
                ...notes  .map(r => ({ store: 'dailyJobNotes',     rec: r })),
                ...signed .map(r => ({ store: 'signedTimesheets',  rec: r })),
            ];
            const baseRetry = Math.max(...allItems.map(x => x.rec.syncRetryCount || 0), 0);
            const nextRetry = baseRetry + 1;
            const isFinal = nextRetry >= MAX_SYNC_RETRIES;
            for (const { store, rec } of allItems) {
                await persistAfterPush(store, rec, {
                    syncStatus: isFinal ? SYNC_STATUS.ERROR : SYNC_STATUS.PENDING,
                    syncRetryCount: (rec.syncRetryCount || 0) + 1,
                    syncErrorMessage: error.message || 'Network unavailable',
                    syncErrorCode: `HTTP_${status || 'NETWORK'}`,
                    lastSyncAttempt: new Date().toISOString(),
                });
            }
            this.scheduleBackoff(nextRetry);
            throw error;
        }
    }

    /**
     * Получить серверные обновления для указанной недели и смержить их с локальными.
     * Merge-стратегия: Last-Write-Wins по updatedAt.
     */
    async fetchAndMergeServerUpdates(weekEndingDate, sinceIso = null) {
        if (!this.isOnline) return { merged: 0, conflicts: 0 };

        try {
            const params = { weekEnding: weekEndingDate };
            if (sinceIso) params.since = sinceIso;

            const response = await axiosInstance.get('/timesheet/updates', { params });
            const serverRecords = response.data?.records || [];

            let merged = 0;
            let conflicts = 0;
            for (const serverRec of serverRecords) {
                const result = await mergeSingleRecord(serverRec);
                if (result === 'merged') merged++;
                else if (result === 'conflict') conflicts++;
            }
            return { merged, conflicts };
        } catch (error) {
            console.error('[TimesheetSync] fetchAndMergeServerUpdates failed:', error);
            return { merged: 0, conflicts: 0, error: error.message };
        }
    }

    /** Ручной запуск — кнопка "Sync now" / "Retry failed" в UI. */
    async forceSyncNow({ healFirst = true } = {}) {
        if (!this.isOnline) {
            throw new Error('Cannot sync while offline');
        }
        this.backoffUntil = 0;
        if (healFirst) {
            await timesheetService.resetErrorsToRetry({ maxCycles: HEAL_MAX_CYCLES });
        }
        await this.tick();
    }

    /** Пересчитать счётчики для UI. Кнопка "Retry" и бейдж используют.
     *  Считает из всех трёх stores — records / dailyNotes / signedTimesheets. */
    async refreshCounters() {
        const stores = [TIMESHEET_RECORD_STORE, DAILY_JOB_NOTE_STORE, SIGNED_TIMESHEET_STORE];
        try {
            let pending = 0, errors = 0, conflicts = 0;
            for (const store of stores) {
                const [p, e, c] = await Promise.all([
                    timesheetService.getRecordsBySyncStatus(SYNC_STATUS.PENDING, store),
                    timesheetService.getRecordsBySyncStatus(SYNC_STATUS.ERROR, store),
                    timesheetService.getRecordsBySyncStatus(SYNC_STATUS.CONFLICT, store),
                ]);
                pending += p.length;
                errors += e.length;
                conflicts += c.length;
            }
            this.pendingCount = pending;
            this.errorCount = errors;
            this.conflictCount = conflicts;
        } catch (e) {
            // non-fatal
        }
    }

    /**
     * Сколько изменений таймшита ещё не дошло до сервера: часы, заметки, подписи
     * (ожидают, в пути, с ошибкой) и операции каталога cost-кодов. Выход из
     * учётки стирает базу устройства — это всё пропало бы.
     */
    async countUnsent() {
        const stores = [TIMESHEET_RECORD_STORE, DAILY_JOB_NOTE_STORE, SIGNED_TIMESHEET_STORE];
        let n = 0;
        for (const store of stores) {
            for (const st of [SYNC_STATUS.PENDING, SYNC_STATUS.SYNCING, SYNC_STATUS.ERROR]) {
                n += (await timesheetService.getRecordsBySyncStatus(st, store)).length;
            }
        }
        return n + getJobCodeOpsQueue().length;
    }

    /**
     * Перед выходом: если есть связь — отправить всё, что ещё лежит на устройстве.
     * Ошибочные возвращаем в очередь (как healTick). Возвращает, сколько осталось.
     */
    async flushBeforeLogout() {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return this.countUnsent();
        try {
            for (const store of [TIMESHEET_RECORD_STORE, DAILY_JOB_NOTE_STORE, SIGNED_TIMESHEET_STORE]) {
                for (const r of await timesheetService.getRecordsBySyncStatus(SYNC_STATUS.ERROR, store)) {
                    await persistToStore(store, { ...r, syncStatus: SYNC_STATUS.PENDING, syncRetryCount: 0 });
                }
            }
            // Пачками, пока что-то уходит (не больше 10 заходов).
            for (let i = 0; i < 10; i++) {
                const res = await this.syncPendingRecords();
                if (!res || (res.synced || 0) === 0) break;
            }
        } catch (e) {
            console.warn('[TimesheetSync] flush before logout failed:', e);
        }
        return this.countUnsent();
    }

    getStatus() {
        return {
            isOnline: this.isOnline,
            isSyncing: this.isSyncing,
            inBackoff: Date.now() < this.backoffUntil,
            backoffRemainingMs: Math.max(0, this.backoffUntil - Date.now()),
            pendingCount: this.pendingCount,
            errorCount: this.errorCount,
            conflictCount: this.conflictCount,
            batchSize: this.currentBatchSize,
        };
    }
}

// ─── Helpers ─────────────────────────────────────────────────────────────

/** Поля, которые НЕ отправляются на сервер. */
function stripLocalOnlyFields(record) {
    const {
        syncStatus, syncErrorMessage, syncErrorCode, syncRetryCount,
        syncConflictDetails, lastSyncAttempt, globalErrorCount,
        employeeDate,
        ...forServer
    } = record;
    // localVersion на сервер передаётся — он нужен для LWW сравнения
    return forServer;
}

/**
 * Запись в том виде, в каком её ждёт сервер. Temp на клиенте — отрицательный
 * employeeId (-tempId): так его держат грид, лимит 24 ч и списки бригады. Сервер
 * различает людей полями employeeType/tempId — без них он искал сотрудника
 * «#-603» и отбивал запись.
 */
function toServerShape(record) {
    const forServer = stripLocalOnlyFields(record);
    if (Number(forServer.employeeId) < 0) {
        return { ...forServer, employeeType: 'temp', tempId: -Number(forServer.employeeId), employeeId: null };
    }
    return forServer;
}

/** Обратно: temp с сервера (tempId, employeeId = null) → -tempId, как на клиенте. */
function withClientSubject(serverRec) {
    if (serverRec.tempId != null && serverRec.employeeId == null) {
        return { ...serverRec, employeeId: -Math.abs(Number(serverRec.tempId)) };
    }
    return serverRec;
}

async function persistRaw(record) {
    return persistToStore(TIMESHEET_RECORD_STORE, record);
}

/** Записать в нужный store с синтетическим индексом employeeDate для records. */
/**
 * Записать итог отправки по снимку записи — только если её не правили, пока
 * снимок летел на сервер. Правили (на айпаде это секунды) — свежую правку не
 * трогаем: она уйдёт следующей отправкой; берём от сервера только serverId.
 * Раньше итог писался поверх по старому снимку, и правка молча откатывалась:
 * «поставил 6 — через миг снова 7» (10/15).
 */
async function persistAfterPush(storeName, snapshot, patch) {
    const db = await initializeDB();
    const tx = db.transaction(storeName, 'readwrite');
    const current = await tx.store.get(snapshot.clientId);
    const record = recordAfterPush(current, snapshot, patch, SYNC_STATUS);
    if (storeName === TIMESHEET_RECORD_STORE && record.employeeId != null && record.workDate) {
        record.employeeDate = `${record.employeeId}|${record.workDate}`;
    }
    await tx.store.put(record);
    await tx.done;
}

async function persistToStore(storeName, record) {
    const db = await initializeDB();
    const tx = db.transaction(storeName, 'readwrite');
    if (storeName === TIMESHEET_RECORD_STORE && record.employeeId != null && record.workDate) {
        record.employeeDate = `${record.employeeId}|${record.workDate}`;
    }
    await tx.store.put(record);
    await tx.done;
}

async function markBatchSyncStatus(records, status) {
    const db = await initializeDB();
    const tx = db.transaction(TIMESHEET_RECORD_STORE, 'readwrite');
    for (const r of records) {
        await tx.store.put({ ...r, syncStatus: status });
    }
    await tx.done;
}

/** Помечаем пачки пачек — по одному store за транзакцию. */
async function markSyncingAcrossStores({ records, notes, signed }) {
    const db = await initializeDB();
    const put = async (storeName, items) => {
        if (!items.length) return;
        const tx = db.transaction(storeName, 'readwrite');
        for (const r of items) {
            const cur = await tx.store.get(r.clientId);
            if (cur && (String(cur.updatedAt || '') !== String(r.updatedAt || '') || (cur.localVersion || 0) !== (r.localVersion || 0))) continue;
            await tx.store.put({ ...r, syncStatus: SYNC_STATUS.SYNCING });
        }
        await tx.done;
    };
    await put(TIMESHEET_RECORD_STORE, records);
    await put(DAILY_JOB_NOTE_STORE, notes);
    await put(SIGNED_TIMESHEET_STORE, signed);
}

async function resetAllToPending({ records, notes, signed }) {
    for (const r of records) await persistAfterPush(TIMESHEET_RECORD_STORE, r, { syncStatus: SYNC_STATUS.PENDING });
    for (const r of notes)   await persistAfterPush(DAILY_JOB_NOTE_STORE,   r, { syncStatus: SYNC_STATUS.PENDING });
    for (const r of signed)  await persistAfterPush(SIGNED_TIMESHEET_STORE, r, { syncStatus: SYNC_STATUS.PENDING });
}

/**
 * РАЗОВАЯ чистка чужих записей из локальной базы.
 *
 * Сервер больше не отдаёт устройству часы других форманов (выборка в
 * fetchUpdatedRecords идёт по автору), но на устройствах, работавших до этой
 * версии, они уже лежат в IndexedDB и никуда сами не денутся: pull их не
 * пришлёт, а значит и не перезапишет.
 *
 * Это МИГРАЦИЯ, а не постоянный слой. Поэтому здесь, один раз, по флагу — а не
 * фильтром по автору в каждом чтении: именно размазанный по коду фильтр и был
 * причиной того, что правка ячейки уходила в чужую запись.
 *
 * Свои pending-записи не трогаем ни при каких условиях: они ещё не уехали.
 *
 * @returns {Promise<number>} сколько записей удалено
 */
export async function purgeForeignRecords() {
    if (localStorage.getItem(FOREIGN_PURGE_KEY)) return 0;

    const myUserId = timesheetService.getCurrentUserMeta().userId;
    if (myUserId == null) return 0;   // не знаем, кто мы — чистить нечего и опасно

    const db = await initializeDB();
    let removed = 0;
    for (const store of [TIMESHEET_RECORD_STORE, DAILY_JOB_NOTE_STORE, SIGNED_TIMESHEET_STORE]) {
        const tx = db.transaction(store, 'readwrite');
        for (const rec of await tx.store.getAll()) {
            if (rec.createdByUserId == null) continue;                       // импорт — ничей
            if (Number(rec.createdByUserId) === Number(myUserId)) continue;  // свой
            if (rec.syncStatus === SYNC_STATUS.PENDING || rec.syncStatus === SYNC_STATUS.SYNCING) continue;
            await tx.store.delete(rec.clientId);
            removed++;
        }
        await tx.done;
    }
    localStorage.setItem(FOREIGN_PURGE_KEY, new Date().toISOString());
    return removed;
}

async function mergeSingleRecord(serverRec, requestedAt = null) {
    serverRec = withClientSubject(serverRec);
    const db = await initializeDB();
    const tx = db.transaction(TIMESHEET_RECORD_STORE, 'readwrite');

    let local = null;
    if (serverRec.clientId) {
        local = await tx.store.get(serverRec.clientId);
    }
    if (!local && serverRec.serverId) {
        const all = await tx.store.getAll();
        local = all.find(r => r.serverId === serverRec.serverId);
    }

    // Защита от "потерянной" связи с Job/Employee: если на сервере FK стал NULL
    // (job/employee удалён из внешней БД через sync и cascade=SET NULL на
    // timesheet_record), мы НЕ затираем локальные id. UI использует jobId/
    // employeeId для привязки к колонке/строке — если перезаписать null,
    // запись "пропадёт" из таблицы хотя часы есть. Денормализованные имена
    // в записи уже есть (jobCode, jobName, employeeFullName) — связь не теряется.
    if (local) {
        if (serverRec.jobId == null && local.jobId != null) {
            serverRec.jobId = local.jobId;
        }
        if (serverRec.employeeId == null && local.employeeId != null) {
            serverRec.employeeId = local.employeeId;
        }
        if (serverRec.tempId == null && local.tempId != null) {
            serverRec.tempId = local.tempId;
        }
    }

    const toSave = {
        ...serverRec,
        syncStatus: SYNC_STATUS.SYNCED,
        syncRetryCount: 0,
        syncErrorMessage: null,
        lastSyncAttempt: new Date().toISOString(),
        localVersion: (local?.localVersion || 0) + 1,
    };
    if (toSave.employeeId != null && toSave.workDate) {
        toSave.employeeDate = `${toSave.employeeId}|${toSave.workDate}`;
    }

    if (!local) {
        await tx.store.put(toSave);
        await tx.done;
        return 'merged';
    }

    // Ответ запросили ДО того, как наша отправка этой записи прошла (отправка
    // отметила её позже момента запроса), а версия другая — значит, в ответе
    // старое состояние сервера. Не откатываем только что отправленное: следующий
    // pull принесёт актуальное. Иначе «поставил 6 — подтянулось старое 7» (10/15).
    if (requestedAt && local.syncStatus === SYNC_STATUS.SYNCED && local.lastSyncAttempt
        && String(local.lastSyncAttempt) > String(requestedAt)
        && String(local.updatedAt || '') !== String(serverRec.updatedAt || '')
        && !['rejected', 'approved'].includes(serverRec.status)) {
        await tx.done;
        return 'skipped';
    }

    // Server-authoritative workflow transitions. Без этой ветки возможен гонщик:
    //   1. Foreman начинает редактировать часы (local PENDING, updatedAt=T1).
    //   2. Worker открывает /h/{token} и диспутит → сервер ставит status=rejected,
    //      updatedAt=T2 (T2 > T1).
    //   3. Foreman не закончив правку сохранил изменение → local.updatedAt=T3 > T2.
    //   4. Следующий pull: обычный LWW говорит "local новее → skip".
    //      Reject тихо теряется, payroll уходит с disputed hours.
    // Фикс: reject/approve — это workflow-решения (worker portal, admin UI),
    // они ДОЛЖНЫ переписать любой параллельный локальный edit. Часы тоже
    // откатываются к серверной версии — foreman увидит что неделя вернулась
    // в rejected, поправит и пересиг'нет (это вся суть dispute-флоу).
    const SERVER_WINS_STATUSES = ['rejected', 'approved'];
    if (
        SERVER_WINS_STATUSES.includes(serverRec.status) &&
        local.status !== serverRec.status
    ) {
        await tx.store.put(toSave);
        await tx.done;
        return 'merged';
    }

    if (local.syncStatus === SYNC_STATUS.PENDING || local.syncStatus === SYNC_STATUS.SYNCING) {
        const localTime = new Date(local.updatedAt).getTime();
        const serverTime = new Date(serverRec.updatedAt).getTime();

        if (localTime > serverTime) {
            await tx.done;
            return 'skipped';
        }
        await tx.store.put({ ...toSave, syncStatus: SYNC_STATUS.CONFLICT });
        await tx.done;
        return 'conflict';
    }

    await tx.store.put(toSave);
    await tx.done;
    return 'merged';
}

/**
 * Merge SignedTimesheet с сервера в локальную IndexedDB.
 * Частый use-case: сервер soft-delete'ил подпись при payroll-invalidation
 * (юзер поправил hours) → isDeleted=true приезжает сюда, UI видит что неделя
 * больше не подписана, foreman получает кнопку "Sign" снова.
 */
async function mergeSingleSigned(serverRec) {
    const db = await initializeDB();
    const tx = db.transaction(SIGNED_TIMESHEET_STORE, 'readwrite');

    let local = null;
    if (serverRec.clientId) {
        local = await tx.store.get(serverRec.clientId);
    }
    if (!local && serverRec.serverId) {
        const all = await tx.store.getAll();
        local = all.find(r => r.serverId === serverRec.serverId);
    }

    const toSave = {
        ...serverRec,
        syncStatus: SYNC_STATUS.SYNCED,
        syncRetryCount: 0,
        syncErrorMessage: null,
        lastSyncAttempt: new Date().toISOString(),
        localVersion: (local?.localVersion || 0) + 1,
    };

    if (!local) {
        // Новая серверная запись — сохраняем как есть (может быть isDeleted=true,
        // но клиенту это важно знать всё равно)
        await tx.store.put(toSave);
        await tx.done;
        return 'merged';
    }

    if (local.syncStatus === SYNC_STATUS.PENDING || local.syncStatus === SYNC_STATUS.SYNCING) {
        const localTime = new Date(local.updatedAt).getTime();
        const serverTime = new Date(serverRec.updatedAt).getTime();
        if (localTime > serverTime) {
            await tx.done;
            return 'skipped';
        }
        await tx.store.put({ ...toSave, syncStatus: SYNC_STATUS.CONFLICT });
        await tx.done;
        return 'conflict';
    }

    await tx.store.put(toSave);
    await tx.done;
    return 'merged';
}

async function mergeSingleNote(serverRec) {
    serverRec = withClientSubject(serverRec);
    const db = await initializeDB();
    const tx = db.transaction(DAILY_JOB_NOTE_STORE, 'readwrite');

    let local = null;
    if (serverRec.clientId) {
        local = await tx.store.get(serverRec.clientId);
    }

    const toSave = {
        ...serverRec,
        syncStatus: SYNC_STATUS.SYNCED,
        syncRetryCount: 0,
        syncErrorMessage: null,
        lastSyncAttempt: new Date().toISOString(),
        localVersion: (local?.localVersion || 0) + 1,
    };

    if (!local) {
        await tx.store.put(toSave);
        await tx.done;
        return 'merged';
    }

    if (local.syncStatus === SYNC_STATUS.PENDING || local.syncStatus === SYNC_STATUS.SYNCING) {
        const localTime = new Date(local.updatedAt).getTime();
        const serverTime = new Date(serverRec.updatedAt).getTime();
        if (localTime > serverTime) {
            await tx.done;
            return 'skipped';
        }
        await tx.store.put({ ...toSave, syncStatus: SYNC_STATUS.CONFLICT });
        await tx.done;
        return 'conflict';
    }

    await tx.store.put(toSave);
    await tx.done;
    return 'merged';
}

// Singleton — один инстанс на всё приложение
const timesheetSyncService = new TimesheetSyncService();

// ─── Рабочая неделя (сервер решает, какая неделя открыта) ────────────────

/**
 * Последнее известное состояние недель с сервера.
 * @returns {{weekEnding: ?string, weekStart: ?string, openedAt: ?string, closedWeeks: string[]}}
 */
export function getActiveWeekState() {
    try {
        const raw = JSON.parse(localStorage.getItem(ACTIVE_WEEK_KEY));
        if (raw && typeof raw === 'object') {
            return {
                weekEnding: raw.weekEnding || null,
                weekStart: raw.weekStart || null,
                openedAt: raw.openedAt || null,
                closedWeeks: Array.isArray(raw.closedWeeks) ? raw.closedWeeks : [],
            };
        }
    } catch { /* повреждённый кэш — считаем, что сервер ещё не отвечал */ }
    return { weekEnding: null, weekStart: null, openedAt: null, closedWeeks: [] };
}

// ─── Курсоры pull'а ─────────────────────────────────────────────────────

function userScopedKey(base) {
    return `${base}:${localStorage.getItem('userId') || ''}`;
}

/** Лист недели как ключ: сменился лист — неделю тянем целиком. */
function weekScopeKey(employeeIds, tempIds) {
    const e = [...(employeeIds || [])].sort((a, b) => a - b).join(',');
    const t = [...(tempIds || [])].sort((a, b) => a - b).join(',');
    return `${e}|${t}`;
}

function readWeekSyncStore() {
    try {
        const raw = JSON.parse(localStorage.getItem(userScopedKey(WEEK_SYNC_KEY)));
        return raw && typeof raw === 'object' ? raw : {};
    } catch {
        return {};
    }
}

function getWeekSyncState(weekEnding) {
    return readWeekSyncStore()[weekEnding] || null;
}

function saveWeekSyncState(weekEnding, state) {
    const store = { ...readWeekSyncStore(), [weekEnding]: state };
    const keep = Object.keys(store).sort().reverse().slice(0, WEEK_SYNC_WEEKS);
    try {
        localStorage.setItem(userScopedKey(WEEK_SYNC_KEY), JSON.stringify(Object.fromEntries(keep.map(k => [k, store[k]]))));
    } catch { /* нет места — следующий pull недели будет полным, только и всего */ }
}

function getGlobalPullCursor() {
    return localStorage.getItem(userScopedKey(GLOBAL_CURSOR_KEY)) || '';
}

function setGlobalPullCursor(cursor) {
    try { localStorage.setItem(userScopedKey(GLOBAL_CURSOR_KEY), cursor); } catch { /* noop */ }
}

// ─── Часы других форманов (выжимка для показа) ───────────────────────────

// «Чужие» — относительно вошедшего: снимок привязан к его userId, чтобы на общем
// айпаде после смены пользователя не показать прежнему свои же часы как чужие.
let othersHoursCache = { key: null, data: {} };

function othersHoursKey() {
    return `${OTHERS_HOURS_KEY}:${localStorage.getItem('userId') || ''}`;
}

function readOthersHoursStore() {
    const key = othersHoursKey();
    if (othersHoursCache.key === key) return othersHoursCache.data;
    let data = {};
    try {
        const raw = JSON.parse(localStorage.getItem(key));
        if (raw && typeof raw === 'object') data = raw;
    } catch { /* повреждённый кэш — ждём следующий pull */ }
    othersHoursCache = { key, data };
    return data;
}

/**
 * Снимок недели с сервера → в форму грида (формат — othersHoursCodec.js).
 * Держим последние OTHERS_HOURS_WEEKS недель, чтобы офлайн-запуск тоже видел.
 */
function saveOthersHours(weekEnding, snap) {
    const store = { ...readOthersHoursStore() };
    store[weekEnding] = { stamp: snap.stamp || '', rows: decodeOthersHours(weekEnding, snap) };
    const keep = Object.keys(store).sort().reverse().slice(0, OTHERS_HOURS_WEEKS);
    const key = othersHoursKey();
    othersHoursCache = { key, data: Object.fromEntries(keep.map(k => [k, store[k]])) };
    try {
        localStorage.setItem(key, JSON.stringify(othersHoursCache.data));
    } catch { /* переполнен localStorage — в памяти всё равно есть */ }
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(EVT_OTHERS_HOURS_CHANGED, { detail: { weekEnding } }));
    }
}

/** Чужие часы по людям листа за неделю (последний снимок с сервера). */
export function getOthersHours(weekEnding) {
    const snap = readOthersHoursStore()[weekEnding];
    return Array.isArray(snap?.rows) ? snap.rows : [];
}

function getOthersStamp(weekEnding) {
    return readOthersHoursStore()[weekEnding]?.stamp || '';
}

// ─── Каталог cost-кодов: чтение кэша + очередь операций ─────────────────

/** Текущий кэш каталога (из localStorage). @returns {Array<{job,code,description}>} */
export function getJobCodeCatalog() {
    try { return JSON.parse(localStorage.getItem(JOB_CODES_KEY)) || []; } catch { return []; }
}

function getJobCodeOpsQueue() {
    try { return JSON.parse(localStorage.getItem(JOB_CODE_OPS_KEY)) || []; } catch { return []; }
}

function saveJobCodeOpsQueue(q) {
    localStorage.setItem(JOB_CODE_OPS_KEY, JSON.stringify(q || []));
}

/** Коды работ, ждущие отправки на сервер (номер ещё не выдан). @returns {Array<{opId,jobId,job,description}>} */
export function getPendingJobCodeCreates() {
    return getJobCodeOpsQueue().filter(o => o.op === 'create');
}

/**
 * Операция над кодами работ. 'create' — новый код работы: номер выдаёт сервер
 * по порядку (7888-1, 7888-2…), в каталог и в список работ код попадает, когда
 * сервер ответил (см. createdJobs). 'add'/'delete' — прежние операции каталога:
 * оптимистично правим локальный кэш. Очередь уезжает со следующим sync;
 * повтор той же операции (opId) сервер не дублирует.
 * @param {{op:'create'|'add'|'delete', jobId?:number, job:string, code?:string, description?:string}} op
 */
export function enqueueJobCodeOp(op) {
    const opId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : String(Date.now()) + '-' + Math.random().toString(36).slice(2);
    const q = getJobCodeOpsQueue();
    q.push(op.op === 'create'
        ? { opId, op: 'create', jobId: op.jobId, job: String(op.job), code: op.code ? String(op.code) : undefined, description: op.description || null, addToGrid: !!op.addToGrid }
        : { opId, op: op.op, job: String(op.job), code: String(op.code), description: op.description || null });
    saveJobCodeOpsQueue(q);
    if (op.op === 'create') {
        if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(EVT_JOB_CODES_CHANGED));
        timesheetSyncService.handleRecordChanged();
        return opId;
    }

    // Оптимистичное локальное применение (ts кэша не трогаем — TTL живёт своей жизнью).
    const cat = getJobCodeCatalog();
    if (op.op === 'add') {
        if (!cat.some(c => c.job === String(op.job) && c.code === String(op.code))) {
            cat.push({ job: String(op.job), code: String(op.code), description: op.description || null });
        }
    } else if (op.op === 'delete') {
        const i = cat.findIndex(c => c.job === String(op.job) && c.code === String(op.code));
        if (i >= 0) cat.splice(i, 1);
    }
    localStorage.setItem(JOB_CODES_KEY, JSON.stringify(cat));
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(EVT_JOB_CODES_CHANGED));

    timesheetSyncService.handleRecordChanged(); // пинаем sync
    return opId;
}

// Expose в window для DevTools / ручного дебага (не для прод-вызовов)
if (typeof window !== 'undefined') {
    window.timesheetSyncService = timesheetSyncService;
}

export { EVT_SYNC_STATE, EVT_SYNC_COMPLETED, EVT_JOB_CODES_CHANGED, EVT_ACTIVE_WEEK_CHANGED, EVT_OTHERS_HOURS_CHANGED, EVT_CONFLICTS_RESOLVED };

/** Записи «в пути» после перезагрузки → снова в очередь. Возвращает, сколько. */
async function resetStuckSyncing() {
    let n = 0;
    for (const store of [TIMESHEET_RECORD_STORE, DAILY_JOB_NOTE_STORE, SIGNED_TIMESHEET_STORE]) {
        const rows = await timesheetService.getRecordsBySyncStatus(SYNC_STATUS.SYNCING, store);
        for (const r of rows) {
            await persistToStore(store, { ...r, syncStatus: SYNC_STATUS.PENDING });
            n++;
        }
    }
    return n;
}
export default timesheetSyncService;
